/**
 * Robust cross-OS clipboard copy utility.
 *
 * Extracted from ManualUpiPanel — every payment surface that needs to
 * copy text with a subsequent navigation (deep-link, redirect) faces the
 * SAME iOS vs Android quirks:
 *
 *   • Android Chrome (90+): sync execCommand("copy") is the ONLY reliable
 *     path. navigator.clipboard.writeText() is killed if the user-gesture
 *     stack frame ends (which happens the moment we set location.href to
 *     an intent:// URI right after).
 *
 *   • iOS Safari (14+): Apple deprecated execCommand("copy") — it often
 *     returns true but silently does nothing. The supported path is
 *     navigator.clipboard.writeText(), which IS async but Safari grants a
 *     wide "transient activation" window (~5s) so we can AWAIT the resolve
 *     before the navigation fires.
 *
 * `robustSyncCopy` returns Promise<boolean>. Callers MUST await when
 * navigation follows in the same gesture (iOS); they may fire-and-forget
 * when only a UI toast follows.
 */

/**
 * OS detection. iPadOS 13+ Safari reports a Mac UA by default ("desktop
 * class") so we also check `navigator.maxTouchPoints > 1` — desktop Macs
 * always report 0 touch points, iPads always 5. Catches Safari-on-iPad
 * even when the customer has the default "Request Desktop Website" toggle.
 */
export function detectOS() {
    const ua = typeof navigator !== "undefined" ? (navigator.userAgent || "") : "";
    const isIPadOS = typeof navigator !== "undefined"
        && /Macintosh/.test(ua)
        && (navigator.maxTouchPoints || 0) > 1;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || isIPadOS;
    const isAndroid = /android/i.test(ua);
    return { ua, isIOS, isAndroid, isIPadOS };
}

/**
 * Try the execCommand path via a hidden textarea. Returns whether the
 * copy actually landed (as best as we can determine — execCommand can
 * return true on iOS while silently no-op'ing, hence the OS split in the
 * main entry point).
 */
function execCommandCopy(text) {
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;outline:0;background:transparent;font-size:16px;opacity:0.01;z-index:-1";
        document.body.appendChild(ta);
        ta.focus({ preventScroll: true });
        try {
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                const range = document.createRange();
                range.selectNodeContents(ta);
                sel.addRange(range);
            }
        } catch { /* ignore selection setup failure */ }
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}

export async function robustSyncCopy(text) {
    if (!text) return false;
    const { isIOS } = detectOS();

    // iOS path — await the async Clipboard API. Don't bother with
    // execCommand (deprecated; Apple has been silently no-op'ing it for
    // years across iOS 14+). Safari's transient activation window is
    // wide enough that the subsequent location.href still fires the
    // deep-link.
    if (isIOS) {
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch { /* fall through to execCommand best-effort */ }
        }
        // Last-resort execCommand for iOS Safari < 13 (vanishingly rare
        // on iOS today).
        return execCommandCopy(text);
    }

    // Android / desktop path — synchronous execCommand FIRST so the
    // write commits inside the user-gesture frame before any navigation
    // pre-empts it. Also fires async clipboard in parallel as a backup
    // (some Android Chrome builds have flipped which path works; firing
    // both maximises hit rate).
    const ok = execCommandCopy(text);
    if (navigator.clipboard?.writeText) {
        try {
            const p = navigator.clipboard.writeText(text);
            if (p && typeof p.catch === "function") p.catch(() => { /* ignore */ });
        } catch { /* ignore */ }
    }
    return ok;
}
